import { reportError, reportEvent, addBreadcrumb } from '../config/sentry';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogContext {
  component?: string;
  action?: string;
  userId?: string;
  sessionId?: string;
  url?: string;
  userAgent?: string;
  timestamp?: string;
  [key: string]: any;
}

export interface LogEntry {
  level: LogLevel;
  message: string;
  context?: LogContext;
  error?: Error;
  timestamp: string;
  stack?: string;
}

/**
 * Enhanced Logger with structured output and production filtering
 * Integrates with Sentry and provides comprehensive debugging information
 */
export class Logger {
  private static internalInstance: Logger;
  private internalLogBuffer: LogEntry[] = [];
  private readonly internalMaxBufferSize = 100;
  private readonly internalEnabledLevels: Set<LogLevel>;

  constructor() {
    // Configure log levels based on environment
    this.internalEnabledLevels = new Set<LogLevel>(
      process.env.NODE_ENV === 'production'
        ? (['warn', 'error'] as LogLevel[])
        : (['debug', 'info', 'warn', 'error'] as LogLevel[])
    );
  }

  static getInstance(): Logger {
    if (!Logger.internalInstance) {
      Logger.internalInstance = new Logger();
    }
    return Logger.internalInstance;
  }

  /**
   * Log debug information (development only)
   */
  debug(message: string, context?: LogContext): void {
    this.log('debug', message, context);
  }

  /**
   * Log informational messages
   */
  info(message: string, context?: LogContext): void {
    this.log('info', message, context);
  }

  /**
   * Log warning messages
   */
  warn(message: string, context?: LogContext): void {
    this.log('warn', message, context);
  }

  /**
   * Log error messages
   */
  error(message: string, error?: Error, context?: LogContext): void {
    this.log('error', message, context, error);
  }

  /**
   * Core logging method
   */
  private log(level: LogLevel, message: string, context?: LogContext, error?: Error): void {
    if (!this.internalEnabledLevels.has(level)) {
      return;
    }

    const timestamp = new Date().toISOString();
    const enrichedContext = this.enrichContext(context);

    const logEntry: LogEntry = {
      level,
      message,
      context: enrichedContext,
      error,
      timestamp,
      stack: error?.stack,
    };

    // Add to buffer
    this.addToBuffer(logEntry);

    // Console output has been removed to adhere to the "zero-console" policy.
    // The logger now only sends data to Sentry and an in-memory buffer.

    // Send to Sentry for warnings and errors
    if (level === 'warn' || level === 'error') {
      this.sendToSentry(logEntry);
    }

    // Add breadcrumb for debugging context
    addBreadcrumb(
      message,
      enrichedContext?.component || 'general',
      level === 'debug' ? 'info' : (level as any),
      enrichedContext
    );
  }

  /**
   * Enrich context with environment information
   */
  private enrichContext(context: LogContext = {}): LogContext {
    return {
      ...context,
      url: context.url || window.location.href,
      userAgent: context.userAgent || navigator.userAgent,
      timestamp: context.timestamp || new Date().toISOString(),
      sessionId: context.sessionId || this.getSessionId(),
      environment: process.env.NODE_ENV,
      version: process.env.REACT_APP_VERSION || '1.0.0',
    };
  }

  /**
   * Output structured logs to console
   */
  private outputToConsole(entry: LogEntry): void {
    // This method is intentionally left empty and will be removed in a future refactor.
    // All console output is forbidden by the "zero-console" policy.
  }

  /**
   * Send logs to Sentry
   */
  private sendToSentry(entry: LogEntry): void {
    const { level, message, context, error } = entry;

    if (error) {
      reportError(error, context, level as any);
    } else {
      reportEvent(message, context, level as any);
    }
  }

  /**
   * Get console styling for different log levels
   */
  private getConsoleStyle(level: LogLevel): string {
    const styles = {
      debug: 'color: #6b7280; background: #f9fafb; padding: 2px 6px; border-radius: 3px;',
      info: 'color: #2563eb; background: #dbeafe; padding: 2px 6px; border-radius: 3px;',
      warn: 'color: #d97706; background: #fef3c7; padding: 2px 6px; border-radius: 3px;',
      error: 'color: #dc2626; background: #fee2e2; padding: 2px 6px; border-radius: 3px;',
    };

    return styles[level];
  }

  /**
   * Add entry to log buffer
   */
  private addToBuffer(entry: LogEntry): void {
    this.internalLogBuffer.push(entry);

    if (this.internalLogBuffer.length > this.internalMaxBufferSize) {
      this.internalLogBuffer = this.internalLogBuffer.slice(-this.internalMaxBufferSize);
    }
  }

  /**
   * Get session ID for tracking
   */
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('log_session_id');

    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('log_session_id', sessionId);
    }

    return sessionId;
  }

  /**
   * Get recent log entries
   */
  getRecentLogs(count: number = 10): LogEntry[] {
    return this.internalLogBuffer.slice(-count);
  }

  /**
   * Get logs by level
   */
  getLogsByLevel(level: LogLevel): LogEntry[] {
    return this.internalLogBuffer.filter((entry) => entry.level === level);
  }

  /**
   * Clear log buffer
   */
  clearLogs(): void {
    this.internalLogBuffer = [];
  }

  /**
   * Export logs for debugging
   */
  exportLogs(): string {
    return JSON.stringify(this.internalLogBuffer, null, 2);
  }

  /**
   * Performance timing logger
   */
  time(label: string, context?: LogContext): () => void {
    const startTime = performance.now();

    this.debug(`⏱️ Timer started: ${label}`, context);

    return () => {
      const endTime = performance.now();
      const duration = Math.round(endTime - startTime);

      this.info(`⏱️ Timer finished: ${label} (${duration}ms)`, {
        ...context,
        duration,
        startTime,
        endTime,
      });
    };
  }

  /**
   * Track user actions
   */
  trackAction(action: string, context?: LogContext): void {
    this.info(`👤 User action: ${action}`, {
      ...context,
      action,
      category: 'user-interaction',
    });
  }

  /**
   * Track API calls
   */
  trackApiCall(endpoint: string, method: string, status?: number, context?: LogContext): void {
    const level = status && status >= 400 ? 'warn' : 'info';

    this.log(level, `🌐 API call: ${method} ${endpoint}`, {
      ...context,
      endpoint,
      method,
      status,
      category: 'api-call',
    });
  }

  /**
   * Track component lifecycle
   */
  trackComponent(
    component: string,
    event: 'mount' | 'unmount' | 'update',
    context?: LogContext
  ): void {
    this.debug(`🔧 Component ${event}: ${component}`, {
      ...context,
      component,
      event,
      category: 'component-lifecycle',
    });
  }
}

// Create singleton instance
const logger = Logger.getInstance();

// Export convenience methods
export const log = {
  debug: (message: string, context?: LogContext) => logger.debug(message, context),
  info: (message: string, context?: LogContext) => logger.info(message, context),
  warn: (message: string, context?: LogContext) => logger.warn(message, context),
  error: (message: string, error?: Error, context?: LogContext) =>
    logger.error(message, error, context),
  time: (label: string, context?: LogContext) => logger.time(label, context),
  trackAction: (action: string, context?: LogContext) => logger.trackAction(action, context),
  trackApiCall: (endpoint: string, method: string, status?: number, context?: LogContext) =>
    logger.trackApiCall(endpoint, method, status, context),
  trackComponent: (
    component: string,
    event: 'mount' | 'unmount' | 'update',
    context?: LogContext
  ) => logger.trackComponent(component, event, context),
};

export { logger };
export default logger;
